import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import Lenis from "lenis";
import { MarketingNav, SiteFooter } from "@/components/Chrome";
import api, { inr } from "@/lib/api";
import { useI18n } from "@/i18n";

const EASE = [0.22, 1, 0.36, 1];
const HERO_IMG =
  "https://images.unsplash.com/photo-1614963326505-843868e1d83a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHw0fHxjb250ZW50JTIwY3JlYXRvciUyMHNvY2lhbCUyMG1lZGlhJTIwdmlkZW8lMjBlZGl0aW5nfGVufDB8fHx8MTc5MjE3MDQ2N3ww&ixlib=rb-4.1.0&q=85";
const VIDEO_IMG =
  "https://images.unsplash.com/photo-1627244714766-94dab62ed964?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwzfHxjb250ZW50JTIwY3JlYXRvciUyMHNvY2lhbCUyMG1lZGlhJTIwdmlkZW8lMjBlZGl0aW5nfGVufDB8fHx8MTc5MjE3MDQ2N3ww&ixlib=rb-4.1.0&q=85";
const SHOOT_IMG =
  "https://images.unsplash.com/photo-1726066012749-f81bf4422d4e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxjb250ZW50JTIwY3JlYXRvciUyMHNvY2lhbCUyMG1lZGlhJTIwdmlkZW8lMjBlZGl0aW5nfGVufDB8fHx8MTc5MjE3MDQ2N3ww&ixlib=rb-4.1.0&q=85";

function KineticLines({ lines, className = "" }) {
  return (
    <h1 className={`font-display font-extrabold leading-[0.98] tracking-tight ${className}`} data-testid="hero-headline">
      {lines.map((line, i) => (
        <span className="mask-line" key={i}>
          <motion.span
            className="block"
            initial={{ y: "112%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.9, delay: 0.15 + i * 0.12, ease: EASE }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </h1>
  );
}

function Reveal({ children, delay = 0, className = "" }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.7, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

const MARQUEE = ["Script bot", "12 languages", "Hook · Scenes · CTA", "Regional looks", "Shot lists", "Posting checklist", "Tracked links", "Results dashboard", "AI assistant"];

function Marquee() {
  const { t } = useI18n();
  const row = (ariaHidden) => (
    <div aria-hidden={ariaHidden || undefined} className="flex shrink-0 items-center gap-24 pr-24">
      {MARQUEE.map((m, i) => (
        <span key={i} className="flex items-center gap-24 font-mono text-sm uppercase tracking-[0.25em] text-muted">
          {t(m)}
          <span className="h-1.5 w-1.5 rounded-full bg-accent" />
        </span>
      ))}
    </div>
  );
  return (
    <div className="relative overflow-hidden border-y border-line/60 bg-surface py-5" data-testid="marquee">
      <div className="flex w-max animate-marquee">
        {row(false)}
        {row(true)}
      </div>
    </div>
  );
}

const BENTO = [
  {
    id: "script", span: "md:col-span-8 md:row-span-2", title: "Script bot",
    desc: "Describe your product and audience — the bot writes complete short-video scripts with hook, scenes, voiceover and CTA, in several styles and languages at once. Rewrite, spin out variations, export as Word or PDF.",
    img: HERO_IMG, big: true,
  },
  {
    id: "video", span: "md:col-span-4", title: "Video app",
    desc: "Scene board and shot lists for your AI video tools, plus your clip library.",
    phase2: "Video generation arrives in Phase 2",
  },
  {
    id: "translate", span: "md:col-span-4", title: "Translator",
    desc: "Voiceovers adapted — not machine-translated — into regional and continental languages, with local titles and hashtags.",
  },
  {
    id: "look", span: "md:col-span-4", title: "Look bot",
    desc: "Regional casting and styling briefs with image prompts, so every version feels local without stereotypes.",
  },
  {
    id: "shoot", span: "md:col-span-4", title: "Shoot",
    desc: "Posting schedule, step-by-step checklist and tracked links for every platform.",
    img: SHOOT_IMG, phase2: "Auto-posting arrives in Phase 2",
  },
  {
    id: "results", span: "md:col-span-4", title: "Results dashboard",
    desc: "Log weekly views, downloads and subscriptions per platform and region — and see where each number comes from.",
  },
];

function Bento() {
  const { t } = useI18n();
  return (
    <section id="workflow" className="mx-auto max-w-6xl px-5 py-24">
      <Reveal>
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted">{t("The workflow")}</p>
        <h2 className="mt-3 max-w-2xl font-display text-4xl font-extrabold tracking-tight sm:text-5xl">
          {t("Six steps, one workspace.")}
        </h2>
      </Reveal>
      <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-12">
        {BENTO.map((c, i) => (
          <Reveal key={c.id} delay={i * 0.06} className={`${c.span} h-full`}>
            <div
              className={`group relative flex h-full flex-col justify-end overflow-hidden rounded-3xl border border-line bg-surface p-7 shadow-card transition-transform duration-300 hover:-translate-y-1 hover:shadow-lift`}
              data-testid={`bento-${c.id}`}
            >
              {c.img && (
                <div className="absolute inset-x-0 top-0 h-40 overflow-hidden opacity-90">
                  <img src={c.img} alt="" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent to-surface" />
                </div>
              )}
              <div className={c.img ? "mt-32" : ""}>
                <h3 className="font-display text-xl font-extrabold">{t(c.title)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted">{t(c.desc)}</p>
                {c.phase2 && (
                  <span className="mt-4 inline-block rounded-full border border-warn/40 bg-warn/10 px-3 py-1 font-mono text-[11px] text-warn" data-testid={`bento-${c.id}-phase2`}>
                    {t(c.phase2)}
                  </span>
                )}
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export default function Landing() {
  const { t, tf } = useI18n();
  const reduced = useReducedMotion();
  const heroRef = useRef(null);
  const [config, setConfig] = useState(null);
  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [0, 600], [0, 80]);

  useEffect(() => {
    if (reduced) return;
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let rafId;
    const raf = (t2) => {
      lenis.raf(t2);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, [reduced]);

  useEffect(() => {
    api.get("/billing/config").then((r) => setConfig(r.data)).catch(() => setConfig(null));
  }, []);

  const monthly = config?.monthly || { price: 999, total: 117882 };
  const yearly = config?.yearly || { price: 9990, total: 1178820 };
  const packs = config?.packs || [
    { price: 1999, total: 235882 }, { price: 2999, total: 353882 },
    { price: 3999, total: 471882 }, { price: 4999, total: 589882 },
  ];
  const money = (p) => "₹" + (p / 100).toLocaleString("en-IN", { minimumFractionDigits: 2 });

  const FAQ = [
    ["Does Jobky make the videos?", "Not yet. Today the bot writes scripts, shot lists, translations, regional look briefs, posting plans and checklists. Video generation, dubbing and auto-posting are greyed out — they arrive in Phase 2 and will be charged from your wallet."],
    ["What does the plan include?", "Every tab of the bot and the AI assistant, rate-limited to keep it fast for everyone. Script generation, translation, look prompts and the assistant are free inside the plan."],
    ["How does the wallet work?", "Buy a credit pack (₹1,999–₹4,999 + GST) and the pack amount lands in your wallet. It is used only for paid actions as they go live, and expires 12 months after each recharge."],
    ["Can I cancel?", "Yes — cancel a monthly subscription any time from the Billing page; you keep access until the end of the paid period."],
  ];

  return (
    <div className="min-h-screen bg-bg" data-testid="landing-page">
      <MarketingNav />

      {/* Hero */}
      <section className="relative overflow-hidden" ref={heroRef} data-testid="hero">
        <div className="mx-auto grid max-w-6xl items-center gap-12 px-5 pb-20 pt-16 md:pt-24 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="font-mono text-xs uppercase tracking-[0.3em] text-accent"
            >
              {t("Script → Video → Translate → Look → Shoot → Results")}
            </motion.p>
            <div className="mt-5 text-4xl sm:text-6xl lg:text-7xl">
              <KineticLines lines={["Your short-video workflow,", "from first script", "to results."].map((l) => t(l))} />
            </div>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.7, ease: EASE }}
              className="mt-6 max-w-xl text-lg text-muted"
            >
              {t("Jobky writes the scripts, plans the scenes, adapts every voiceover into regional languages, briefs the shoot and tracks what each post delivers — with an AI assistant on tap.")}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.85, ease: EASE }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/signup"
                data-testid="hero-cta-start"
                className="rounded-full bg-accent px-8 py-3.5 text-sm font-bold text-white shadow-lift transition-transform hover:-translate-y-0.5"
              >
                {t("Sign up")}
              </Link>
              <a
                href="#pricing"
                data-testid="hero-cta-pricing"
                className="rounded-full border border-ink/20 bg-surface px-8 py-3.5 text-sm font-bold text-ink transition-colors hover:border-accent hover:text-accent"
              >
                {t("See pricing")}
              </a>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1.05 }}
              className="mt-10 flex flex-wrap gap-2"
            >
              {["Hook in 2 seconds", "One plan, every feature", "GST invoices included"].map((chip) => (
                <span key={chip} className="rounded-full border border-line bg-surface px-4 py-1.5 font-mono text-xs text-muted">
                  {t(chip)}
                </span>
              ))}
            </motion.div>
          </div>
          <div className="relative lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.5, ease: EASE }}
              style={{ y: reduced ? 0 : imgY }}
              className="relative"
            >
              <div className="overflow-hidden rounded-3xl border border-line shadow-lift">
                <img src={HERO_IMG} alt="Creator editing a short video" className="aspect-[4/5] w-full object-cover" />
              </div>
              <div className="absolute -left-4 top-8 animate-floaty rounded-2xl border border-line bg-surface/95 px-4 py-3 shadow-card backdrop-blur">
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted">{t("Step 1 · Script bot")}</p>
                <p className="mt-1 text-sm font-bold">{t("Hook written · 5 scenes · CTA")}</p>
              </div>
              <div className="absolute -right-3 bottom-10 animate-floaty rounded-2xl border border-line bg-surface/95 px-4 py-3 shadow-card backdrop-blur" style={{ animationDelay: "1.4s" }}>
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted">{t("Step 3 · Translator")}</p>
                <p className="mt-1 text-sm font-bold">{t("Marathi · Tamil · Bengali ready")}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Marquee />

      <Bento />

      {/* Pricing */}
      <section id="pricing" className="border-t border-line/60 bg-surface py-24" data-testid="pricing-section">
        <div className="mx-auto max-w-6xl px-5">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted">{t("Pricing")}</p>
            <h2 className="mt-3 font-display text-4xl font-extrabold tracking-tight sm:text-5xl">{t("One plan. Every feature.")}</h2>
            <p className="mt-3 max-w-xl text-muted">{t("All prices exclude 18% GST, added at checkout. Every first payment includes ₹200 of wallet credit, once.")}</p>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            <Reveal>
              <div className="flex h-full flex-col rounded-3xl border border-line bg-bg p-8" data-testid="plan-monthly">
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted">{t("Monthly")}</p>
                <p className="mt-4 font-display text-5xl font-extrabold">₹{monthly.price.toLocaleString("en-IN")}</p>
                <p className="mt-1 text-sm text-muted">{tf("+ 18% GST · {amount} / month", { amount: money(monthly.total) })}</p>
                <ul className="mt-6 space-y-2 text-sm text-muted">
                  <li>· {t("· Every tab of the bot, with rate-limited AI")}</li>
                  <li>· {t("· Renews monthly by autopay, cancel any time")}</li>
                  <li>· {t("· First payment includes ₹200 wallet credit, once")}</li>
                </ul>
                <Link to="/signup" data-testid="pricing-monthly-cta" className="mt-8 inline-block rounded-full bg-accent px-7 py-3 text-center text-sm font-bold text-white transition-transform hover:-translate-y-0.5">
                  {t("Start monthly")}
                </Link>
              </div>
            </Reveal>
            <Reveal delay={0.08}>
              <div className="flex h-full flex-col rounded-3xl border-2 border-accent bg-bg p-8 shadow-lift" data-testid="plan-yearly">
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent">{t("Yearly · 10 months for 12")}</p>
                <p className="mt-4 font-display text-5xl font-extrabold">₹{yearly.price.toLocaleString("en-IN")}</p>
                <p className="mt-1 text-sm text-muted">{tf("+ 18% GST · {amount} billed once a year", { amount: money(yearly.total) })}</p>
                <ul className="mt-6 space-y-2 text-sm text-muted">
                  <li>· {t("· Everything in the monthly plan")}</li>
                  <li>· {t("· Two months free vs monthly")}</li>
                  <li>· {t("· First payment includes ₹200 wallet credit, once")}</li>
                </ul>
                <Link to="/signup" data-testid="pricing-yearly-cta" className="mt-8 inline-block rounded-full bg-accent px-7 py-3 text-center text-sm font-bold text-white transition-transform hover:-translate-y-0.5">
                  {t("Start yearly")}
                </Link>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <div className="mt-14">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted">{t("Wallet credit packs · + 18% GST at checkout")}</p>
              <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {packs.map((p, i) => (
                  <div key={p.price} className="rounded-2xl border border-line bg-bg p-5" data-testid={`pricing-pack-${i}`}>
                    <p className="font-display text-2xl font-extrabold">₹{p.price.toLocaleString("en-IN")}</p>
                    <p className="mt-1 text-xs text-muted">{tf("+ 18% GST · you pay {amount}", { amount: money(p.total) })}</p>
                    <p className="mt-3 text-sm text-muted">{tf("Wallet +{amount}", { amount: "₹" + p.price.toLocaleString("en-IN") })}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-5 py-24" data-testid="faq-section">
        <Reveal>
          <h2 className="font-display text-4xl font-extrabold tracking-tight">{t("Honest answers")}</h2>
        </Reveal>
        <div className="mt-8 space-y-3">
          {FAQ.map(([q, a], i) => (
            <Reveal key={i} delay={i * 0.05}>
              <details className="group rounded-2xl border border-line bg-surface p-5" data-testid={`faq-${i}`}>
                <summary className="cursor-pointer list-none font-bold marker:hidden">
                  <span className="mr-2 font-mono text-accent">0{i + 1}</span>{t(q)}
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-muted">{t(a)}</p>
              </details>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-line/60 bg-night py-20 text-center grain relative" data-testid="cta-band">
        <Reveal>
          <h2 className="mx-auto max-w-2xl px-5 font-display text-4xl font-extrabold tracking-tight text-night-ink sm:text-5xl">
            {t("Your next reel starts with a script.")}
          </h2>
          <Link to="/signup" data-testid="footer-cta" className="mt-8 inline-block rounded-full bg-accent px-9 py-4 text-sm font-bold text-white shadow-lift transition-transform hover:-translate-y-0.5">
            {t("Create your account")}
          </Link>
        </Reveal>
      </section>

      <SiteFooter />
    </div>
  );
}
